<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-row items-center justify-between">
      <Headline tag="span" look="h5">
        {{ $t('reading_aids_selector.diopter') }}
      </Headline>
      <div class="flex flex-row gap-2">
        <ProductDetailDescriptionText>
          {{ $t('reading_aids_selector.info_text') }}
        </ProductDetailDescriptionText>
        <FimButton
          type="link"
          :no-padding="true"
          :aria-label="$t('store_finder.details_page.content.more_info')"
          @click="showInfoOverlay"
        >
          <template #icon>
            <LazyIconFielmannInfoCircle class="size-5" />
          </template>
        </FimButton>
      </div>
    </div>

    <HorizontalItemsScroller
      ref="slider"
      class="sibling-slider"
      :start-at-index="sliderIndex"
    >
      <ScrollerItem
        v-for="variant in variantsSortedByDiopter"
        :key="variant.id"
        class="group px-2"
      >
        <FimButton
          v-if="getVariantDiopters(variant)"
          no-padding
          :type="variant.id === currentVariantId ? 'primary' : 'secondary'"
          class="rounded p-3 transition-colors"
          data-testid="select-variant"
          :class="{
            'opacity-30':
              !isVariantAvailable(product, variant) && !ignoreSoldOut,
          }"
          @click="setVariantIdUrl(variant.id)"
        >
          <div>
            <p v-if="getVariantDiopters(variant)?.label" class="text-center">
              {{ getVariantDiopters(variant)?.label }}
              {{ $t('reading_aids_selector.diopter_unit') }}
            </p>
          </div>
        </FimButton>
      </ScrollerItem>
    </HorizontalItemsScroller>
    <ClientOnly>
      <Overlay
        :title="$t('pdp.glasses.overlay.reader_glasses_diopter_title')"
        :open="isInfoOverlayVisible"
        portal-target="#info-overlay"
        @update:open="isInfoOverlayVisible = $event"
      >
        <h2 class="mb-4 text-[24px] font-medium leading-[32px]">
          {{ $t('pdp.glasses.overlay.reader_glasses_diopter_heading') }}
        </h2>
        <div
          v-dompurify-html="
            $t('pdp.glasses.overlay.reader_glasses_diopter_text')
          "
          class="mb-4 text-lg leading-6"
        ></div>
      </Overlay>
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import type { Variant, Product } from '@scayle/storefront-core'
import FimButton from '~/components/ui/buttons/FimButton.vue'

const props = defineProps({
  product: {
    type: Object as PropType<Product>,
    required: true,
  },
  ignoreSoldOut: {
    type: Boolean,
    default: false,
  },
})

const router = useRouter()

const isInfoOverlayVisible = ref(false)

const showInfoOverlay = () => {
  isInfoOverlayVisible.value = true
}

const setVariantIdUrl = async (variantId: number) => {
  await router.push({ query: { variantid: variantId } })
}

const getDiopterValue = (variant: Variant) =>
  +(getVariantDiopters(variant)?.value ?? '-1000000')

const variantsSortedByDiopter = computed(() => {
  const variants = props.product.variants ?? []
  return variants.sort((a, b) => getDiopterValue(a) - getDiopterValue(b))
})

const currentVariantId = computed(
  () => +(router.currentRoute.value.query.variantid as string),
)

onBeforeMount(async () => {
  if (!currentVariantId.value) {
    await router.replace({
      query: { variantid: variantsSortedByDiopter.value[0].id },
    })
  }
})

const sliderIndex = computed(() => {
  const currentVariantIndex = (variantsSortedByDiopter.value ?? []).findIndex(
    (variant: Variant) => {
      return variant.id === currentVariantId.value
    },
  )
  return Math.max(currentVariantIndex, 0)
})
</script>

<style lang="scss" scoped>
/* stylelint-disable rule-empty-line-before */
.sibling-slider :deep(.slider-btn-prev:disabled),
.sibling-slider :deep(.slider-btn-next:disabled) {
  opacity: 0.2 !important;
}

.sibling-slider :deep(.wrapper) {
  &::before {
    display: none;
  }
  &::after {
    display: none;
  }
}
.sibling-slider :deep(.scroller) {
  margin-left: 2.25rem;
  width: calc(100% - 4.5rem);
  padding-bottom: 0.5rem;
  padding-top: 0.5rem;
}
</style>

<style>
/* stylelint-disable */
.sibling-slider {
  container-type: inline-size;
}
/* stylelint-enable */
</style>
